import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";
import type { Channel } from "../chatTypes";

/**
 * Drag-to-reorder: moving a channel within or between folders (desktop column and the phone drawer); saves the whole
 * space's order to the server and then refreshes the list.
 */
export function useChannelReorder({ socket, currentVoidId, channels, setChanOrder }: {
  socket: { emit: (event: string, payload?: unknown) => unknown };
  currentVoidId: string;
  channels: Channel[];
  setChanOrder: Dispatch<SetStateAction<Record<string, string[]>>>;
}) {
  const handleChannelReorder = useCallback(async (channelId: string, targetFolderId: string | null, beforeChannelId: string | null) => {
    if (!currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      const byFolder = new Map<string, Channel[]>();
      channels.forEach((c) => {
        const fid = c.folderId || '';
        if (!byFolder.has(fid)) byFolder.set(fid, []);
        byFolder.get(fid)!.push(c);
      });
      // Capture target list before removal to keep relative indices
      const targetKey = targetFolderId || '';
      const targetListOriginal = byFolder.get(targetKey) || [];
      const removedIdx = targetListOriginal.findIndex(c => c.id === channelId);
      const insertIdxOriginal = beforeChannelId ? targetListOriginal.findIndex(c => c.id === beforeChannelId) : -1;

      // Remove from all lists
      byFolder.forEach((list, fid) => {
        byFolder.set(fid, list.filter(c => c.id !== channelId));
      });
      const targetList = byFolder.get(targetKey) || [];
      // If dragging downward onto an item, insert *after* that item
      let insertIdx = insertIdxOriginal;
      if (insertIdxOriginal >= 0 && removedIdx >= 0 && removedIdx < insertIdxOriginal) {
        insertIdx = insertIdxOriginal; // target shifted left by one; this index lands after it
      } else if (insertIdxOriginal >= 0) {
        insertIdx = targetList.findIndex(c => c.id === beforeChannelId);
      }
      const channelMeta = channels.find(c => c.id === channelId);
      const toInsert: Channel = channelMeta ? { ...channelMeta, folderId: targetFolderId || null } : { id: channelId, name: '', folderId: targetFolderId || null };
      if (insertIdx >= 0) targetList.splice(insertIdx, 0, toInsert);
      else targetList.push(toInsert);
      byFolder.set(targetKey, targetList);

      const payload: { channelId: string; folderId: string | null; position: number }[] = [];
      byFolder.forEach((list, fid) => {
        list.forEach((c, idx) => {
          payload.push({ channelId: c.id, folderId: fid || null, position: (list.length - idx) * 10 });
        });
      });
      await api.patchAuth(`/spaces/${encodeURIComponent(currentVoidId)}/channels/reorder`, { channels: payload }, tok);
      socket.emit("channel:list", { voidId: currentVoidId });
      setChanOrder((prev) => ({ ...prev, [currentVoidId]: payload.map(p => p.channelId) }));
    } catch (e:any) {
      toast(e?.message || 'Failed to reorder channels', 'error');
    }
  }, [currentVoidId, channels, socket, setChanOrder]);

  return { handleChannelReorder };
}
